/**
 * React Query hook for global search.
 */

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import {
  search,
  SearchResponse,
  SEARCH_DEFAULT_LIMIT,
  SEARCH_MIN_TERM_LENGTH,
} from "./client";

export const queryKeys = {
  search: (term: string, limit: number) =>
    ["global-search", term, limit] as const,
};

const EMPTY_RESULT: SearchResponse = { clients: [], rfps: [] };

/** Trims and lower-cases the term so cache keys and requests agree. */
export const normalizeSearchTerm = (raw: string): string =>
  raw.trim().toLowerCase();

export const isSearchableTerm = (term: string): boolean =>
  term.length >= SEARCH_MIN_TERM_LENGTH;

/**
 * Fetches clients and RFPs matching `term`. Terms shorter than
 * SEARCH_MIN_TERM_LENGTH never hit the network and resolve to empty groups,
 * so callers can bind the input value straight through.
 */
export const useGlobalSearch = (
  term: string,
  limit: number = SEARCH_DEFAULT_LIMIT,
): UseQueryResult<SearchResponse, Error> => {
  const axiosInstance = useApi();
  const normalized = normalizeSearchTerm(term);
  const enabled = isSearchableTerm(normalized);

  return useQuery({
    queryKey: queryKeys.search(normalized, limit),
    queryFn: ({ signal }) => search(normalized, axiosInstance, limit, signal),
    enabled,
    // The previous result stays on screen while the next keystroke's query
    // is in flight, so the dropdown does not flicker empty between terms.
    placeholderData: (previous) => previous ?? EMPTY_RESULT,
    staleTime: 30 * 1000,
  });
};
